import {computed, onMounted, shallowRef} from 'vue';

/**
 * The first frame of every page with the app bar (X128, Grant 2026-10-26 "Yes fix it").
 *
 * Vuetify 3.12 lays a v-app out from things it only learns once the app is mounted:
 * - VAppBar's height is read from its toolbar's template ref (vToolbarRef.contentHeight). Refs are never set while
 *   the server renders, nor during the client's hydrating render, so v-main is drawn with `--v-layout-top: 0px` and
 *   the page sits under the bar until the ref arrives after hydration, when everything drops 64px.
 * - A layout item's width, top and height (createLayout's layoutItemStyles) are only written after mount. Before
 *   that a navigation drawer is as wide as its content, so the AppLayout menu (wider than its 290px translate)
 *   showed a strip on the left, and the admin side menu started under the app bar.
 *
 * So until the layout has mounted, each layout passes the values Vuetify will compute once it has, as inline styles
 * (props.style comes after Vuetify's own in every one of these components, so it wins). The server and the hydrating
 * client render the same thing (no mismatch); on mount Vuetify's own values take over in the same flush, and they
 * are the same numbers, so nothing moves. Anything that depends on the screen width (the admin drawer is permanent
 * from 960px, an off-canvas overlay below) is done in CSS media queries while booting, never with useDisplay():
 * with `ssr: true` Vuetify's display is width 0 (xs, mobile) on the server and during hydration, and only learns the
 * real width at app:suspense:resolve.
 */

// VToolbar's default height (density default, no extension); both app bars use it on every screen size.
export const APP_BAR_HEIGHT = 64;
// The AppLayout menu (its width prop).
export const APP_DRAWER_WIDTH = 290;
// The admin side menu: Vuetify's default drawer width (no width prop), and the screen width it is permanent from
// (Vuetify's md threshold; AdminLayout's own isMobile is innerWidth < 960).
export const ADMIN_DRAWER_WIDTH = 256;
export const ADMIN_DRAWER_FROM = 960;

/** v-main's inline style before mount: the app bar's offset Vuetify cannot see yet. */
export const mainBootStyle = (booted) => booted ? undefined : {'--v-layout-top': `${APP_BAR_HEIGHT}px`};

/** A drawer's inline style before mount: its real width, and below the app bar like it will be. */
export const drawerBootStyle = (booted, width) => booted ? undefined : {
    width: `${width}px`,
    top: `${APP_BAR_HEIGHT}px`,
    height: `calc(100% - ${APP_BAR_HEIGHT}px)`,
};

export function useLayoutBoot() {
    const booted = shallowRef(false);
    onMounted(() => {
        booted.value = true;
    });
    return {
        booted,
        mainStyle: computed(() => mainBootStyle(booted.value)),
        drawerStyle: (width) => computed(() => drawerBootStyle(booted.value, width)),
    };
}
